import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { Box, CircularProgress } from "@mui/material";

import ChartControls from "../../components/Channel/ChartControls";
import MultiLineAvgChart from "../../components/charts3/MultiLineAvgChart";

import { useSocialMediaData } from "../../hooks/useSocialMediaData";
import { useSocialMinuteCompare } from "../../hooks/useSocialMinuteCompare";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger"; // ✅ LOGGER

export default function ChannelSocialMediaCompare({
  channelId,
  channelInfo,
  rawVideo,
  refreshKey,
  refreshEnabled,
  setRefreshEnabled,
  refreshIntervalMin,
  setRefreshIntervalMin,
  refreshCountdown,
  manualRefresh
}) {
  const MODE = "minute";

  const { logPageChange, logTimeframeChange } = useUserJourneyLogger({
    channelId,
    channelName: channelInfo?.name || channelInfo?.n || "unknown",
  });

  /* =========================
     PAGE LOG (ONCE)
  ========================= */
  useEffect(() => {
    logPageChange({
      chartType: "socialmediacompare",
      mode: "default",
    });
  }, [logPageChange]);

  /* =========================
     TIMEFRAME LOG (STATIC)
  ========================= */
  useEffect(() => {
    logTimeframeChange({
      chartType: "socialmediacompare",
      timeframeValue: -1,
      timeframeLabel: "minute",
    });
  }, [logTimeframeChange]);

  /* =========================
     DATE (SINGLE DAY)
  ========================= */

  const today = dayjs().format("YYYY-MM-DD");
  const [date, setDate] = useState(today);

  /* =========================
     TIME RANGE
  ========================= */

  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");

  /* =========================
     DATA SOURCES
  ========================= */


  // rawVideo is now received as a prop

  const { youtubeData, twitterData, loading: loadingSocial } =
    useSocialMediaData(channelId, "minute", date, date, refreshKey, channelInfo?.ytChannelId || null);

  // loadingVideo is not defined, so we set it to false (or pass as prop if needed)
  const loadingVideo = false;

  /* =========================
     MINUTE ENGINE
  ========================= */

  const series = useSocialMinuteCompare({
    rawVideo,
    youtubeData,
    twitterData,
    selectedDate: date,
    startTime,
    endTime,
  });

  const hasData = series.some(
    (s) => Array.isArray(s.data) && s.data.length > 0
  );

  /* =========================
     RENDER
  ========================= */

  return (
    <Box p={2}>
      <ChartControls
        channelId={channelId}
        mode={MODE}
        hideModeButtons
        startDateInput={date}
        endDateInput={date}
        onStartDateChange={setDate}
        onEndDateChange={setDate}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
      />

      {loadingVideo || loadingSocial ? (
        <CircularProgress />
      ) : hasData ? (
        <MultiLineAvgChart
          title="TV, YouTube, Twitter/X"
          series={series}
          colors={["#3587d7", "#ff0033", "#000000"]}
          refreshKey={refreshKey}
        />
      ) : (
        <div style={{ padding: 24, color: "#6b7280" }}>
          No data available for selected day/time.
        </div>
      )}
    </Box>
  );
}
